import { noop } from 'rxjs';
import { wrapToArray, serializeSimpleChanges, selectObjectProps } from '@noda-lib/utils/handlers';
function Bind(ctx) {
  return (target, methodName, descriptor) => {
    const {
      value: originalMethod
    } = descriptor;
    return {
      get() {
        return originalMethod.bind(ctx ?? this);
      }
    };
  };
}
function Cache(isClearCacheBeforeDestroy) {
  return function (target, methodName, descriptor) {
    const {
      value: originalMethod
    } = descriptor;
    const store = new Map();
    if (isClearCacheBeforeDestroy) {
      const {
        ngOnDestroy: originalDestroy
      } = target;
      target.ngOnDestroy = function () {
        store.clear();
        originalDestroy?.call(this);
      };
    }
    return {
      value: function (...args) {
        const jsonArgs = JSON.stringify(args);
        const isExistValue = store.has(jsonArgs);
        if (isExistValue) {
          return store.get(jsonArgs);
        }
        const value = originalMethod.apply(this, args);
        store.set(jsonArgs, value);
        return value;
      }
    };
  };
}
function Enumerable() {
  return function (target, methodName, descriptor) {
    return {
      ...descriptor,
      enumerable: true
    };
  };
}
const INITIALIZE_HOOKS_META = '__initializeHooks__';
// TODO передалать в соответсвии с логикой Destroy декоратора
function Initialize() {
  return function (target, methodName, descriptor) {
    const prototype = target;
    const isExistMethodArray = INITIALIZE_HOOKS_META in prototype;
    if (!isExistMethodArray) {
      const {
        ngOnInit: originalOnInit
      } = prototype;
      prototype[INITIALIZE_HOOKS_META] = [];
      prototype.ngOnInit = function () {
        if (originalOnInit) {
          originalOnInit.call(this);
        }
        prototype[INITIALIZE_HOOKS_META].forEach(methodName => {
          this[methodName]();
        });
      };
    }
    prototype[INITIALIZE_HOOKS_META].push(methodName);
    return descriptor;
  };
}
const CHANGES_HOOK_PROXY_META = '__onChangesHookProxyMeta__';
class OnChangesHookProxy {
  static registerProxy(prototype) {
    if (OnChangesHookProxy.isRegisteredProxy(prototype)) {
      console.warn('OnChangesHookProxy method has been register previously');
    } else {
      prototype[CHANGES_HOOK_PROXY_META] = [];
      prototype.ngOnChanges = new Proxy(prototype.ngOnChanges ?? noop, {
        apply(originalNgOnChanges, ctx, args) {
          originalNgOnChanges.apply(ctx, args);
          prototype[CHANGES_HOOK_PROXY_META].forEach(decorator => {
            decorator.onChanges(args[0], ctx, prototype);
          });
        }
      });
    }
  }
  static registerDecorator(prototype, decorator) {
    if (!OnChangesHookProxy.isRegisteredDecorator(prototype, decorator)) {
      prototype[CHANGES_HOOK_PROXY_META].push(decorator);
    } else {
      console.warn(`OnChanges decorator "${decorator.name}" has been register previously`);
    }
  }
  static isRegisteredProxy(prototype) {
    return CHANGES_HOOK_PROXY_META in prototype;
  }
  static getDecoratorByName(prototype, candidateName) {
    const decorator = prototype[CHANGES_HOOK_PROXY_META].find(d => d.name === candidateName);
    return decorator;
  }
  static isRegisteredDecorator(prototype, candidate) {
    return Boolean(this.getDecoratorByName(prototype, candidate.name));
  }
  static checkAndRegisterProxy(prototype) {
    if (!OnChangesHookProxy.isRegisteredProxy(prototype)) {
      OnChangesHookProxy.registerProxy(prototype);
    }
  }
  static checkAndRegisterDecorator(prototype, decorator) {
    OnChangesHookProxy.checkAndRegisterProxy(prototype);
    if (!OnChangesHookProxy.isRegisteredDecorator(prototype, decorator)) {
      OnChangesHookProxy.registerDecorator(prototype, decorator);
    }
  }
  static patchDecoratorMeta(prototype, name, meta) {
    const decorator = OnChangesHookProxy.getDecoratorByName(prototype, name);
    if (decorator) {
      decorator.patchMeta(meta, prototype);
    } else {
      console.warn(`Decorator "${name}" not found`);
    }
  }
}

/* eslint-disable @/brace-style */
const ON_CHANGES_META = '__onChange__';
const ON_CHANGES_DECORATOR = 'OnChangeDecorator';
class OnChangeDecorator {
  constructor() {
    // eslint-disable-next-line @typescript-eslint/typedef
    this.name = ON_CHANGES_DECORATOR;
  }
  isRegisteredMeta(prototype) {
    return ON_CHANGES_META in prototype;
  }
  registerMeta(prototype) {
    if (this.isRegisteredMeta(prototype)) {
      console.warn('OnChangeDecorator meta has been register previously');
    } else {
      prototype[ON_CHANGES_META] = [];
    }
  }
  registerMethod(meta, prototype) {
    if (!this.isRegisteredMethod(meta, prototype)) {
      prototype[ON_CHANGES_META].push(meta);
    }
  }
  isRegisteredMethod(meta, prototype) {
    const methodMeta = prototype[ON_CHANGES_META].find(m => m.methodName === meta.methodName);
    return Boolean(methodMeta);
  }
  checkAndRegisterMeta(meta, prototype) {
    if (!this.isRegisteredMeta(prototype)) this.registerMeta(prototype);
    if (!this.isRegisteredMethod(meta, prototype)) this.registerMethod(meta, prototype);
  }
  patchMeta(meta, prototype) {
    this.checkAndRegisterMeta(meta, prototype);
  }
  isDependsOn(dependsOn, changes) {
    if (typeof dependsOn === 'string') {
      dependsOn = wrapToArray(dependsOn);
    }
    return dependsOn.some(d => d in changes);
  }
  onChanges(simpleChanges, ctx, prototype) {
    const changes = serializeSimpleChanges(simpleChanges);
    prototype[ON_CHANGES_META].filter(({
      dependsOn
    }) => this.isDependsOn(dependsOn, changes)).forEach(({
      methodName,
      dependsOn
    }) => {
      const params = typeof dependsOn === 'string' ? changes[dependsOn] : selectObjectProps(changes, dependsOn);
      ctx[methodName](params);
    });
  }
}
function OnChange(dependsOn) {
  return function (target, methodName) {
    const prototype = target;
    const meta = {
      methodName,
      dependsOn
    };
    OnChangesHookProxy.checkAndRegisterDecorator(prototype, new OnChangeDecorator());
    OnChangesHookProxy.patchDecoratorMeta(prototype, ON_CHANGES_DECORATOR, meta);
  };
}
const DESTROY_HOOK_PROXY_META = '__destroyHookProxyMeta__';
class OnDestroyHookProxy {
  static registerProxy(prototype) {
    if (OnDestroyHookProxy.isRegisteredDestroyProxy(prototype)) {
      console.warn('OnDestroyHookProxy method has been register previously');
    } else {
      prototype[DESTROY_HOOK_PROXY_META] = [];
      prototype.ngOnDestroy = new Proxy(prototype.ngOnDestroy ?? noop, {
        apply(originalNgOnDestroy, ctx) {
          originalNgOnDestroy.apply(ctx);
          prototype[DESTROY_HOOK_PROXY_META].forEach(decorator => {
            decorator.onDestroy(ctx, prototype);
          });
        }
      });
    }
  }
  static registerDecorator(prototype, decorator) {
    if (!OnDestroyHookProxy.isRegisteredDecorator(prototype, decorator)) {
      prototype[DESTROY_HOOK_PROXY_META].push(decorator);
    } else {
      console.warn(`Destroy decorator "${decorator.name}" has been register previously`);
    }
  }
  static isRegisteredDestroyProxy(prototype) {
    return DESTROY_HOOK_PROXY_META in prototype;
  }
  static getDecoratorByName(prototype, candidateName) {
    const decorator = prototype[DESTROY_HOOK_PROXY_META].find(d => d.name === candidateName);
    return decorator;
  }
  static isRegisteredDecorator(prototype, candidate) {
    return Boolean(this.getDecoratorByName(prototype, candidate.name));
  }
  static checkAndRegisterProxy(prototype) {
    if (!OnDestroyHookProxy.isRegisteredDestroyProxy(prototype)) {
      OnDestroyHookProxy.registerProxy(prototype);
    }
  }
  static checkAndRegisterDecorator(prototype, decorator) {
    OnDestroyHookProxy.checkAndRegisterProxy(prototype);
    if (!OnDestroyHookProxy.isRegisteredDecorator(prototype, decorator)) {
      OnDestroyHookProxy.registerDecorator(prototype, decorator);
    }
  }
  static patchDecoratorMeta(prototype, name, meta) {
    const decorator = OnDestroyHookProxy.getDecoratorByName(prototype, name);
    if (decorator) {
      decorator.patchMeta(meta, prototype);
    } else {
      console.warn(`Decorator "${name}" not found`);
    }
  }
}
const DESTROY_EFFECTS_META = '__destroyEffects__';
const DESTROY_EFFECTS_DECORATOR = 'DestroyEffectDecorator';
class DestroyEffectDecorator {
  constructor() {
    // eslint-disable-next-line @typescript-eslint/typedef
    this.name = DESTROY_EFFECTS_DECORATOR; // * Maybe lately DestroyEffectDecorator.name (from constructor)
  }
  isRegisteredMeta(prototype) {
    return DESTROY_EFFECTS_META in prototype;
  }
  registerMeta(prototype) {
    if (this.isRegisteredMeta(prototype)) {
      console.warn('DestroyEffectDecorator meta has been register previously');
    } else {
      prototype[DESTROY_EFFECTS_META] = [];
    }
  }
  registerEffect(effectName, prototype) {
    if (!this.isRegisteredEffect(effectName, prototype)) {
      prototype[DESTROY_EFFECTS_META].push(effectName);
    }
  }
  isRegisteredEffect(effectName, prototype) {
    return prototype[DESTROY_EFFECTS_META].includes(effectName);
  }
  checkAndRegisterMeta(effectName, prototype) {
    if (!this.isRegisteredMeta(prototype)) this.registerMeta(prototype);
    if (!this.isRegisteredEffect(effectName, prototype)) this.registerEffect(effectName, prototype);
  }
  patchMeta(effectName, prototype) {
    this.checkAndRegisterMeta(effectName, prototype);
  }
  onDestroy(ctx, prototype) {
    prototype[DESTROY_EFFECTS_META].forEach(effectKey => {
      const isExistEffect = effectKey in ctx;
      if (isExistEffect) ctx[effectKey].destroy();else console.warn(`Effect ${effectKey} not found`);
    });
  }
}
function DestroyEffect() {
  return function (target, propertyName) {
    const prototype = target;
    OnDestroyHookProxy.checkAndRegisterDecorator(prototype, new DestroyEffectDecorator());
    OnDestroyHookProxy.patchDecoratorMeta(prototype, DESTROY_EFFECTS_DECORATOR, propertyName);
  };
}
const DESTROY_INSTANCES_META = '__destroyInstanceMethods__';
const DESTROY_METHODS_DECORATOR = 'DestroyMethodDecorator';
class DestroyMethodDecorator {
  constructor() {
    // eslint-disable-next-line @typescript-eslint/typedef
    this.name = DESTROY_METHODS_DECORATOR;
  }
  isRegisteredMeta(prototype) {
    return DESTROY_INSTANCES_META in prototype;
  }
  isRegisteredMethod(methodName, prototype) {
    return prototype[DESTROY_INSTANCES_META].includes(methodName);
  }
  registerMeta(prototype) {
    if (this.isRegisteredMeta(prototype)) {
      console.warn('DestroyMethodDecorator meta has been register previously');
    } else {
      prototype[DESTROY_INSTANCES_META] = [];
    }
  }
  registerMethod(methodName, prototype) {
    if (!this.isRegisteredMethod(methodName, prototype)) {
      prototype[DESTROY_INSTANCES_META].push(methodName);
    }
  }
  checkAndRegisterMeta(methodName, prototype) {
    if (!this.isRegisteredMeta(prototype)) this.registerMeta(prototype);
    if (!this.isRegisteredMethod(methodName, prototype)) this.registerMethod(methodName, prototype);
  }
  patchMeta(methodName, prototype) {
    this.checkAndRegisterMeta(methodName, prototype);
  }
  onDestroy(ctx, prototype) {
    prototype[DESTROY_INSTANCES_META].forEach(methodName => {
      const isExistEffect = methodName in ctx;
      if (isExistEffect) ctx[methodName]();else console.warn(`Method ${methodName} not found`);
    });
  }
}
function Destroy() {
  return function (target, methodName) {
    const prototype = target;
    OnDestroyHookProxy.checkAndRegisterDecorator(prototype, new DestroyMethodDecorator());
    OnDestroyHookProxy.patchDecoratorMeta(prototype, DESTROY_METHODS_DECORATOR, methodName);
  };
}

/**
 * Generated bundle index. Do not edit.
 */

export { Bind, Cache, Destroy, DestroyEffect, Enumerable, Initialize, OnChange };
